/**
 * Departments remembered on this phone or tablet (native app only).
 *
 * Every department is its own server with its own accounts, so a member of
 * two departments holds two separate sign-ins. The app keeps one department
 * active -- `scenelightServerUrl`, which every fetch is sent to (main.jsx) --
 * and parks the others' state until they are switched back to.
 *
 * "State" is everything in localStorage except the keys below that belong to
 * the device rather than to a department: the sign-in token, cached roles,
 * cached Okta settings, a station's PIN device token and so on. Parking the
 * whole remainder, rather than a list of known keys, means a key added later
 * can't leak from one department into another.
 *
 * Switching never signs out of the department left behind, so its
 * notifications keep arriving (each carries its department's code; see
 * departmentForNotification).
 */

const LIST_KEY = 'scenelightDepartments'
const ACTIVE_KEY = 'scenelightServerUrl'
const PENDING_LINK_KEY = 'scenelightPendingDeepLink'
const STATE_PREFIX = 'scenelightDeptState:'

// Belong to the device, not to a department
const DEVICE_KEYS = new Set([
  LIST_KEY, ACTIVE_KEY, PENDING_LINK_KEY,
  'theme', 'sidebarCollapsed', 'biometricDeviceId'
])
const isDeviceKey = (key) => DEVICE_KEYS.has(key) || key.startsWith(STATE_PREFIX)

const normUrl = (url) => String(url || '').trim().replace(/\/+$/, '')

export function listDepartments(storage = localStorage) {
  try {
    const list = JSON.parse(storage.getItem(LIST_KEY) || '[]')
    return Array.isArray(list) ? list.filter(d => d && typeof d.url === 'string') : []
  } catch {
    return []
  }
}

const saveList = (list, storage) => storage.setItem(LIST_KEY, JSON.stringify(list))

export const activeDepartmentUrl = (storage = localStorage) => normUrl(storage.getItem(ACTIVE_KEY))

export function activeDepartment(storage = localStorage) {
  const url = activeDepartmentUrl(storage)
  return listDepartments(storage).find(d => d.url === url) || null
}

/** Add or update a department in the list (name and code fill in when known). */
export function rememberDepartment({ url, name, code }, storage = localStorage) {
  const u = normUrl(url)
  if (!u) return
  const list = listDepartments(storage)
  const existing = list.find(d => d.url === u)
  if (existing) {
    if (name) existing.name = name
    if (code) existing.code = String(code).toLowerCase()
  } else {
    list.push({ url: u, name: name || null, code: code ? String(code).toLowerCase() : null })
  }
  saveList(list, storage)
}

/** Move the active department's state out of localStorage, into its parking slot. */
export function parkActiveDepartment(storage = localStorage) {
  const url = activeDepartmentUrl(storage)
  const keys = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && !isDeviceKey(key)) keys.push(key)
  }
  if (url) {
    const state = Object.fromEntries(keys.map(k => [k, storage.getItem(k)]))
    storage.setItem(STATE_PREFIX + url, JSON.stringify(state))
  }
  keys.forEach(k => storage.removeItem(k))
  storage.removeItem(ACTIVE_KEY)
}

/** Make `url` the active department, bringing back its parked state if any. */
export function activateDepartment(url, storage = localStorage) {
  const u = normUrl(url)
  let state = {}
  try { state = JSON.parse(storage.getItem(STATE_PREFIX + u) || '{}') || {} } catch { state = {} }
  for (const [k, v] of Object.entries(state)) {
    if (!isDeviceKey(k) && typeof v === 'string') storage.setItem(k, v)
  }
  storage.removeItem(STATE_PREFIX + u)
  storage.setItem(ACTIVE_KEY, u)
}

/** The parked sign-in token of a department that is not active. */
export function parkedToken(url, storage = localStorage) {
  try {
    return JSON.parse(storage.getItem(STATE_PREFIX + normUrl(url)) || '{}')?.localAuthToken || null
  } catch {
    return null
  }
}

/** Drop a department that is not active, and everything parked for it. */
export function forgetDepartment(url, storage = localStorage) {
  const u = normUrl(url)
  if (u === activeDepartmentUrl(storage)) return
  saveList(listDepartments(storage).filter(d => d.url !== u), storage)
  storage.removeItem(STATE_PREFIX + u)
}

/**
 * The remembered department a tapped notification came from, when it isn't
 * the active one. Only a department already on this device can be switched
 * to; a notification can't point the app at a new server.
 */
export function departmentForNotification(code, storage = localStorage) {
  if (!code) return null
  const c = String(code).toLowerCase()
  const active = activeDepartment(storage)
  if (active?.code === c) return null
  return listDepartments(storage).find(d => d.code === c && d.url !== activeDepartmentUrl(storage)) || null
}

/** Page to open after the next start (set before switching for a notification). */
export function setPendingDeepLink(path, storage = localStorage) {
  if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) storage.setItem(PENDING_LINK_KEY, path)
}

export function takePendingDeepLink(storage = localStorage) {
  const path = storage.getItem(PENDING_LINK_KEY)
  storage.removeItem(PENDING_LINK_KEY)
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : null
}
